import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  TreeDotsIcon,
  Menu,
  List,
  ListItem,
  ConfirmDialog,
  TrashIcon,
  EditIcon,
  MarkerIcon,
  ListSeparator,
  IconButton,
} from "@activecollab/components";
import classnames from "classnames";
import PropTypes from "prop-types";
import { CreateTaskOption } from "./MessageOptions/index.jsx";

export const MessageOptions = ({ id, onDelete, className, onClose, onEdit, onMarkAsUnread, onOpen }) => {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleOpen = useCallback(() => {
    setOpen(true);
    typeof onOpen === "function" && onOpen();
  }, [onOpen]);

  const handleClose = useCallback(() => {
    setOpen(false);
    typeof onClose === "function" && onClose();
  }, [onClose]);

  const handleShowDeleteModal = useCallback(() => {
    setShowDeleteModal(true);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
  }, []);

  const handleDeleteSuccess = useCallback(() => {
    typeof onDelete === "function" && onDelete(id);
    handleCloseDeleteModal();
    handleClose();
  }, [handleClose, handleCloseDeleteModal, id, onDelete]);

  const handleEdit = useCallback(() => {
    typeof onEdit === "function" && onEdit(id);
    setOpen(false);
  }, [id, onEdit]);

  const handleMarkAsUnread = useCallback(() => {
    typeof onMarkAsUnread === "function" && onMarkAsUnread();
    setOpen(false);
  }, [onMarkAsUnread]);

  return (
    <>
      <Menu
        target={
          <IconButton
            variant="text gray"
            active={open}
            size="small"
            className={classnames("target-message-opts", { open }, className)}
          >
            <TreeDotsIcon />
          </IconButton>
        }
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        menuClassName="tw-mt-1"
        mode="tiny"
      >
        <List className="tw-py-2">
          <CreateTaskOption messageId={id} />
          <ListSeparator />
          {typeof onEdit === "function" ? (
            <ListItem onClick={handleEdit}>
              <EditIcon className="tw-mr-2 fill-900" /> {t("Edit")}
            </ListItem>
          ) : null}
          {typeof onMarkAsUnread === "function" ? (
            <ListItem onClick={handleMarkAsUnread}>
              <MarkerIcon className="tw-mr-2 fill-900" /> {t("Mark as Unread")}
            </ListItem>
          ) : null}
          {typeof onDelete === "function" ? (
            <ListItem onClick={handleShowDeleteModal}>
              <TrashIcon className="tw-mr-2 fill-900" /> {t("Delete")}
            </ListItem>
          ) : null}
        </List>
      </Menu>
      <ConfirmDialog
        open={showDeleteModal}
        dialogTitle={t("Delete message?")}
        dialogContent={t("This will permanently delete message for all participants.")}
        confirmBtnText={t("Delete")}
        cancelBtnText={t("Cancel")}
        onCancel={handleCloseDeleteModal}
        onConfirm={handleDeleteSuccess}
      />
    </>
  );
};

MessageOptions.propTypes = {
  id: PropTypes.number.isRequired,
  onDelete: PropTypes.func,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
  onMarkAsUnread: PropTypes.func,
  onEdit: PropTypes.func,
  className: PropTypes.string,
};
